<template>
  <div
    class="ms-grid-viewer flex"
    :class="{
      selectedTextMode: selectedTextMode,
      'flex-column': flex,
      'has-scroll-x': hasScrollX,
      'has-scroll-y': hasScrollY,
      'has-paging': pagination,
      'has-footer': footer,
      'row-span-group-mode': rowSpanGroup && groupField,
    }"
    :style="borderStyle"
    @keydown="handleGridKeydown"
    tabindex="0"
  >
    <!-- Border trái cố định -->
    <div v-if="showBorder" class="ms-grid-left-border"></div>

    <!-- Border phải cố định -->
    <div v-if="showBorder" class="ms-grid-right-border"></div>

    <div
      :class="['ms-content--table', {'no-data': dataEmpty}, stickeyCountClass, {scroller: flex && !dataEmpty}]"
      :style="[gridViewStyle]"
      ref="content"
      @scroll="tableScroll"
    >
      <div class="grid-scroll">
        <table class="ms-table" cellpadding="0" cellspacing="0">
          <thead class="ms-thead" v-show="isShowHeader" :class="{multi: headers.length > 1}" ref="thead">
            <template v-for="(headerItem, i1) in headers" :key="i1">
              <draggable
                v-if="dataDragable"
                tag="tr"
                v-model="headerDrag"
                class="ms-tr"
                @change="dragChanged"
                draggable="th[field]"
              >
                <th
                  class="ms-th multiple-cell sticky ms-th-col"
                  v-if="multiple && i1 === 0"
                  :rowspan="0"
                  :key="0"
                  scope="col"
                >
                  <ms-checkbox
                    v-model="checkedAll"
                    :indeterminate="headerIndeterminate"
                    @click="changeCheckedMultiple"
                    @update:indeterminate="updateHeaderIndeterminate"
                    class="justify-center"
                    checkOnly
                  />
                </th>

                <th
                  class="ms-th serial text-center ms-th-col"
                  :style="{left: serialOffsetLeft}"
                  :class="{sticky: hasLockColumn}"
                  v-if="serial && i1 === 0"
                  :rowspan="0"
                >
                  {{ $t('i18nComponent.HeaderOption.SortOrderColumn') }}
                </th>

                <template v-for="(col, i2) in headers[0]" :key="i2">
                  <th
                    :class="['ms-th ms-col-th ms-col-th-span', i2 === headerItem.length - 1 ? 'ms-col-th-last' : '']"
                    v-if="col.isGroup"
                    :colspan="col.colspan"
                  >
                    <div class="ms-th-content flex-row">
                      <div class="flex flex-row">
                        <div
                          v-if="col.lockable !== false"
                          class="icon24 lock"
                          :class="{locked: col.lock}"
                          @click="columnChangeLock(col)"
                        ></div>
                        <span class="flex">{{ col.caption }}</span>
                      </div>
                    </div>
                  </th>
                  <ms-th
                    v-else
                    ref="th"
                    :class="['ms-th', '' + col.dataField, i2 === headerItem.length - 1 ? 'ms-col-th-last' : '']"
                    :rowspan="i1"
                    :key="col.dataField"
                    :col="col"
                    :resizeCol="resizeCol"
                    :filterable="filterable"
                    @filter="columnFilter"
                    :moduleAutocomplete="moduleAutocomplete"
                    @resizeOff="resizeClose"
                    @sort="sort"
                    scope="col"
                    @changeColumnResize="changeColumnResize"
                    @changeLock="columnChangeLock"
                    @resizeOn="handlerResizeOn"
                    :hideIconLock="true"
                    :canShowContainerEmpty="canShowContainerEmpty"
                  ></ms-th>
                </template>

                <slot name="widget-title-th" :rowspan="0" v-if="$slots['widget-title-th'] && i1 === 0" />
                <th
                  class="ms-th widget-title ms-th-col text-center"
                  v-else-if="$slots['widget-title'] && i1 === 0"
                  :rowspan="0"
                >
                  <slot name="widget-title" />
                </th>
              </draggable>
              <tr v-else class="ms-tr" :class="`tr-${i1 + 1}`">
                <th class="ms-th multiple-cell sticky ms-th-col" v-if="multiple && i1 === 0" :rowspan="0">
                  <ms-checkbox
                    v-model="checkedAll"
                    :indeterminate="headerIndeterminate"
                    @click="changeCheckedMultiple"
                    @update:indeterminate="updateHeaderIndeterminate"
                    class="justify-center"
                    checkOnly
                  />
                </th>

                <th
                  :style="{left: serialOffsetLeft}"
                  class="ms-th serial text-center ms-th-col"
                  :class="{sticky: hasLockColumn}"
                  v-if="serial && i1 === 0"
                  :rowspan="headers.length"
                >
                  {{ $t('i18nComponent.HeaderOption.SortOrderColumn') }}
                </th>

                <template v-for="(col, i2) in headerItem" :key="i2">
                  <th
                    :class="['ms-th ms-col-th ms-col-th-span', i2 === headerItem.length - 1 ? 'ms-col-th-last' : '']"
                    v-if="col.isGroup"
                    :colspan="col.colspan"
                  >
                    <div class="ms-th-content flex-row">
                      <div class="flex flex-row">
                        <div
                          v-if="col.lockable !== false && !col.group"
                          class="icon24 lock"
                          :class="{locked: col.lock}"
                          @click="columnChangeLock(col)"
                        ></div>
                        <span class="flex">{{ col.caption }}</span>
                      </div>
                    </div>
                  </th>
                  <ms-th
                    v-else
                    ref="th"
                    :class="['ms-th', i2 === headerItem.length - 1 ? 'ms-col-th-last' : '']"
                    :rowspan="0"
                    :key="col.dataField"
                    :col="col"
                    :resizeCol="resizeCol"
                    :filterable="filterable"
                    @filter="columnFilter"
                    :moduleAutocomplete="moduleAutocomplete"
                    @resizeOff="resizeClose"
                    @sort="sort"
                    @changeColumnResize="changeColumnResize"
                    @changeLock="columnChangeLock"
                    @resizeOn="handlerResizeOn"
                    :hideIconLock="true"
                    :canShowContainerEmpty="canShowContainerEmpty"
                  ></ms-th>
                </template>

                <slot name="widget-title-th" :rowspan="0" v-if="$slots['widget-title-th'] && i1 === 0" />
                <th
                  class="ms-th widget-title ms-th-col text-center"
                  v-else-if="$slots['widget-title'] && i1 === 0"
                  :rowspan="0"
                >
                  <slot name="widget-title" />
                </th>
              </tr>
            </template>
          </thead>
          <transition mode="out-in">
            <tbody v-if="loading">
              <tr v-for="n in shimmerRow" :key="n" :index="n">
                <td
                  v-for="(sc, i) in shimmerColumn"
                  :key="i"
                  :class="{sticky: sc.lock}"
                  :style="{
                    left: typeof sc.offset == 'number' ? sc.offset + 'px' : 'unset',
                    borderRight: '1px dotted #c1c4cc',
                  }"
                >
                  <div class="shimmer"></div>
                </td>
              </tr>
            </tbody>
          </transition>
          <transition name="trans-content-table" mode="out-in">
            <!-- v-on:before-enter="beforeRenderContent" -->
            <!-- <template v-if="loading">
              <tbody class="ms-tbody grid-loading" :key="loading"></tbody>
            </template> -->
            <tbody
              class="ms-tbody data"
              @click="quickSelected"
              v-if="!dataEmpty && !loading"
              @keyup="destroyQuickSelected"
              :key="loading"
            >
              <template v-if="groupField && rowSpanGroup">
                <template v-for="(group, indexRow) in groupDataRowSpan" :key="[indexRow].join('-')">
                  <ms-tr-row-span
                    ref="tr"
                    :isSelected="isSelected(group)"
                    :isCheckedAll="checkedAll"
                    :columns="columnx"
                    :index="indexRow"
                    :dataRow="group"
                    :serial="serial"
                    :pageSize="pageSize"
                    :currentPage="currentPage"
                    :multiple="multiple"
                    :rowSelected="rowSelected.data"
                    v-on="listeners"
                    @dblclick="$emit('rowDbClick', group, $event)"
                    @focus="onFocusRow(group, $event)"
                    @click="onClickRow(group, $event)"
                    :rowActions="rowActions"
                    :actionInlineCount="actionInlineCount"
                    :disabledCheckedMultipleTbody="funDisabledCheckedMultipleTbody(group)"
                    :class="[
                      {
                        'row-selected': group == rowSelected.data && !shortKeyForQuickSelect,
                      },
                      {
                        'multi-row-selected': shortkeyQuickSelectedData.includes(group),
                      },
                      {'action-pointer': checkListenerDbClick},
                      typeof customClassRow == 'function' ? customClassRow(group) : customClassRow,
                    ]"
                    @styleColumn="styleColumn"
                    :hasLockColumn="hasLockColumn"
                    :groupField="groupField"
                  >
                    <template v-if="$slots['widget-body']" v-slot:widget>
                      <slot name="widget-body" :record="group" />
                    </template>
                  </ms-tr-row-span>
                </template>
              </template>

              <template v-else-if="groupField">
                <template v-for="(group, indexRow) in groupData" :key="[indexRow, group.value].join('-')">
                  <!-- key để phức tạp như vậy để tránh trùng với các đối tượng khác -->
                  <ms-tr-group
                    v-if="group.isGroup"
                    class="ms-tr"
                    :columnSumary="columnSumary"
                    :dataRow="group"
                    @toogleTree="toggleTree"
                  >
                    <template v-if="$slots['widget-body']" v-slot:widget>
                      <slot name="widget-body" :record="group" />
                    </template>
                  </ms-tr-group>
                  <ms-tr
                    v-else
                    ref="tr"
                    :isSelected="isSelected(group)"
                    :isCheckedAll="checkedAll"
                    :key="group[idField]"
                    :columns="columnx"
                    :index="indexRow"
                    :dataRow="group"
                    :serial="serial"
                    :pageSize="pageSize"
                    :currentPage="currentPage"
                    :multiple="multiple"
                    :rowSelected="rowSelected.data"
                    v-on="listeners"
                    @dblclick="$emit('rowDbClick', group, $event)"
                    @focus="onFocusRow(group, $event)"
                    @click="onClickRow(group, $event)"
                    :rowActions="rowActions"
                    :actionInlineCount="actionInlineCount"
                    :disabledCheckedMultipleTbody="funDisabledCheckedMultipleTbody(group)"
                    :class="[
                      {
                        'row-selected': group == rowSelected.data && !shortKeyForQuickSelect,
                      },
                      {
                        'multi-row-selected': shortkeyQuickSelectedData.includes(group),
                      },
                      {'action-pointer': checkListenerDbClick},
                      typeof customClassRow == 'function' ? customClassRow(group) : customClassRow,
                    ]"
                    @styleColumn="styleColumn"
                    :hasLockColumn="hasLockColumn"
                  >
                    <template v-if="$slots['widget-body']" v-slot:widget>
                      <slot name="widget-body" :record="group" />
                    </template>
                  </ms-tr>
                </template>
              </template>
              <template v-else>
                <ms-tr
                  ref="tr"
                  v-for="(dataRow, indexRow) in datax"
                  :isSelected="isSelected(dataRow)"
                  :isCheckedAll="checkedAll"
                  :key="dataRow[idField]"
                  :columns="columnx"
                  :index="indexRow"
                  :dataRow="dataRow"
                  :serial="serial"
                  :pageSize="pageSize"
                  :currentPage="currentPage"
                  :multiple="multiple"
                  :rowSelected="rowSelected.data"
                  v-on="listeners"
                  @dblclick="$emit('rowDbClick', dataRow, $event)"
                  @focus="onFocusRow(dataRow, $event)"
                  @click="onClickRow(dataRow, $event)"
                  :rowActions="rowActions"
                  :actionInlineCount="actionInlineCount"
                  :disabledCheckedMultipleTbody="funDisabledCheckedMultipleTbody(dataRow)"
                  :class="[
                    {
                      'row-selected': dataRow == rowSelected.data && !shortKeyForQuickSelect,
                    },
                    {
                      'multi-row-selected': shortkeyQuickSelectedData.includes(dataRow),
                    },
                    {'action-pointer': checkListenerDbClick},
                    typeof customClassRow == 'function' ? customClassRow(dataRow) : customClassRow,
                  ]"
                  @styleColumn="styleColumn"
                  :hasLockColumn="hasLockColumn"
                >
                  <template v-if="$slots['widget-body']" v-slot:widget>
                    <slot name="widget-body" :record="dataRow" />
                  </template>
                </ms-tr>
              </template>
            </tbody>

            <div v-else-if="isShowImageGridEmpty && !loading" class="grid-no-data">
              <!-- Empty State -->
              <!-- <ms-empty-state
                :entity-name="entityName"
                :variant="variantEmptyState"
                :primary-action="primaryActions"
                @primary-action="handleEmptyStateAction"
              /> -->
            </div>
          </transition>
        </table>

        <div
          class="table-footer"
          ref="tableFooter"
          :class="[stickeyCountClass, {visibility: dataEmpty}]"
          v-if="footer && !loading"
        >
          <table cellspacing="0">
            <tfoot class="ms-footer data" :key="loading">
              <tr v-for="(footerItem, i1) in footers" :key="i1" class="ms-tr">
                <!-- <th
              class="ms-th multiple-cell sticky"
              v-if="multiple && i1 === 0"
              :rowspan="footers.length"
              style="left: 0"
            >
              <ms-checkbox
                :value="checkedAll"
                @click="changeCheckedMultiple"
                class="justify-center"
                checkOnly
                style="display: none"
              />
            </th> -->
                <template v-for="(col, i2) in footerItem" :key="i2">
                  <th
                    v-if="col.isGroup"
                    :colspan="col.colspan"
                    :class="[
                      'ms-th ms-col-th ms-col-th-span',
                      i2 === headerItem.length - 1 ? 'ms-col-th-last' : '',
                      {sticky: typeof col.stickyOffset === 'number'},
                    ]"
                    style="left: 0"
                  >
                    <span>{{ col.caption }}</span>
                  </th>
                  <!-- <th
                    v-else-if="col.stickyRight"
                    class="sticky"
                    style="right: 0"
                    :style="getFooterStyle(col)"
                  ></th> -->
                  <th
                    v-else
                    :colspan="col.colspan"
                    :class="[{sticky: typeof col.stickyOffset === 'number'}, col.dataField]"
                    class="ms-th-viewer static-col"
                    :style="getFooterStyle(col)"
                  >
                    <div class="flex" :class="[`text-${col.align || 'left'}`, col.classes]">
                      <span v-if="typeof col.footerText == 'string'">
                        {{ col.footerText }}
                      </span>
                      <span v-else-if="summaryData" :class="[`text-${col.align || 'left'}`]">{{
                        getSummaryDisplay(col, i1, i2)
                      }}</span>
                      <span
                        v-else
                        :class="[
                          `text-${col.align || 'left'}`,
                          {
                            'footer-total-count': col.summaryType === $ms.constant.SummaryType.RowCount,
                          },
                        ]"
                        v-html="getSummaryDisplay(col, i1, i2)"
                      ></span>
                    </div>
                  </th>
                  <!-- <th
                    v-else
                    class="sticky"
                    style="right: 0"
                    :style="getFooterStyle(col)"
                  ></th> -->
                </template>

                <th
                  class="ms-th widget-title text-center"
                  v-if="widgetOptions && i1 === 0"
                  :rowspan="footers.length"
                  :class="[`text-${widgetOptions.align ? widgetOptions.align : 'right'}`]"
                  :style="[
                    {'min-width': `${widgetOptions.width}px`},
                    columnx.length > 0 ? {width: `${widgetOptions.width}px`} : null,
                  ]"
                ></th>

                <!-- <th
                  class="ms-th widget-title"
                  v-if="col.stickyRight"
                  :style="getFooterStyle(col)"
                ></th> -->

                <!-- <slot
              name="widget-summary-th"
              v-if="$slots['widget-summary-th'] && i1 === 0"
            />
            <th
              v-else-if="$slots['widget-summary'] && i1 === 0"
              class="ms-th widget-title"
              :rowspan="0"
            >
              <slot name="widget-summary" />
            </th> -->
              </tr>
            </tfoot>
          </table>
        </div>

        <ms-pagination
          class="table-paging"
          v-if="pagination && !loadingSummary"
          :pageSize="pageSizeInternal"
          :dataRender="datax.length"
          :pageIndex="currentPage"
          :summary="summary"
          :pageTotal="totalCount"
          @loadData="listenersLoadData"
          @changePageSize="changePageSize"
        />
      </div>
    </div>

    <div v-if="disabled" class="disabled"></div>

    <!-- <div class="loading grid-loading" v-if="loading"> -->
    <!-- <div class="loadingio-spinner-dual-ball-p4fazz803e">
        <div class="ldio-z76ep1av69">
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div> -->
    <!-- <div class="loading-cover"></div>
      <div class="loading-svg">
        <div class="effect-1 effects"></div>
      </div>
    </div> -->
  </div>
</template>
<script>
import msTr from './MsTr.vue';
import msTrGroup from './MsTrGroup.vue';
import msTrRowSpan from './MsTrRowSpan.vue';
import msTh from './MsTh.vue';
import msPagination from './MsPagination.vue';
import {gridGroupData} from '@/mixins/component/gridGroupData';
import {VueDraggableNext} from 'vue-draggable-next';
// import MsEmptyState from '@/components/emptyState/MsEmptyState.vue';
import summaryUtil from '@/commons/summaryUtil';
import {filter} from '@/mixins/common/filter.js';
import {useColumnResize} from '@/setup/grid/resizeColumn.js';
import {getCurrentInstance, nextTick, onMounted} from 'vue';
import commonFn from '@/commons/commonFunction.js';

export default {
  name: 'msGridViewer',
  mixins: [gridGroupData, filter],
  components: {
    msTr,
    msTh,
    msPagination,
    draggable: VueDraggableNext,
    msTrGroup,
    msTrRowSpan,
    // MsEmptyState,
  },
  props: {
    widgetOptions: {},
    flex: {
      default: true,
      type: Boolean,
    },
    rowSpanGroup: {
      default: false,
      type: Boolean,
    },
    /**
     * có show text filter khi lọc ra không
     */
    hasBindingFilterHeader: {
      default: false,
      type: Boolean,
    },
    /**
     * Value thực hiện lưu giá trị các cột được chọn
     */
    selected: {
      type: Array,
      return: () => [],
    },
    //Data dữ liệu grid từ ngoài truyền vào
    data: {
      type: Array,
      return: () => [],
    },
    /**
     * Tên trường khóa chính
     */
    idField: {
      type: String,
    },
    /**
     * nnlam 13/03/2021
     * cấu hình các trường cần sort mặc định khi load trang
     * Kiểu dữ liệu Array chứa danh sách các object, cấu trúc object có ít nhất 2 trường là sortField(tên property cần sort) và sortType (là desc hay asc)
     * ví dụ : [{sortField: 'AssetCode', sortType : this.$ms.constant.SortStatus.asc}]
     */
    defaultSort: {
      type: Array,
      return: () => [],
    },
    //Cấu hình các cột truyền vào
    /**
     * Các thuộc tính trong columns
     * @params dataField Field dữ liệu dùng để nhận giá trị thay đổi và hiện thị.
     * @params title:Tiêu đề của cột
     * @params width:độ rộng của cột
     * @params autoResize:Cột tự động to ra khi màn hình lớn
     * @params formatType:Dữ liệu dùng để format lấy từ trong file enumeration.js FormatType
     * @params template:Sử dụng 1 file khác ở ngoài truyền vào để render
     */
    columns: {
      type: Array,
      return: () => [],
    },
    //Set độ rộng cho grid
    maxHeight: {
      default: null,
      type: [Number, String],
    },
    //Cấu hình có phân trang hay không
    pagination: {
      default: false,
      type: Boolean,
    },
    /*
    Cho phép hiển thị header của lưới hay không? mặc định có
    Create: 14/01/2020
    Author: ĐVThi    
    */
    isShowHeader: {
      default: true,
      type: Boolean,
    },
    //Nhận 2 giá trị remote/local để lấy giá trị ở server hoặc local
    queryMode: {
      default: 'remote',
      type: String,
    },
    //Số bản ghi trên mới trang trường hợp phân trang local
    pageSize: {
      default: 20,
      type: Number,
    },
    //Cột cho phép tích chọn nhiều dòng
    multiple: {
      default: false,
      type: Boolean,
    },
    // Cho phép hiển thị số dòng
    showTotalRecord: {
      default: false,
      type: Boolean,
    },
    indexFlexRight: {
      default: 1,
      type: Number,
    },
    //Tổng số bản ghi
    pageTotal: {
      default: 0,
      type: Number,
    },
    /**
     * thông tin summary
     * trong này sẽ có total luôn
     */
    summary: {},
    //Hiện thị cột số thứ tự
    serial: {
      default: false,
      type: Boolean,
    },
    //Cho phép di chuyển độ rộng của cột hay không
    resizeCol: {
      default: true,
      type: Boolean,
    },
    //Trường để kiểm tra sort dưới client hay trên sever
    //Local/Serve
    sortMode: {
      default: 'remote',
      type: String,
    },
    hasFilterEmpty: {
      default: false,
      type: Boolean,
    },
    loading: {
      default: true,
    },
    loadingSummary: {
      default: true,
    },
    customClassRow: {
      default: null,
    },
    loadChildrenData: {
      default: null,
    },
    /**
     * Action của row
     */
    rowActions: {
      default: null,
      type: [Object, Array],
    },
    /**
     * Số lượng item hiển thị inline khi hover vào dòng
     * nếu vượt quá số lượng này thì sẽ đặt trong more ...
     */
    actionInlineCount: {
      default: 5,
      type: Number,
    },
    /**
     * Disable control
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Số dòng shimmer loading
     */
    shimmerRow: {
      type: Number,
      default: 6,
    },
    /*
     * thêm cấu hình những cột luôn lấy khi load grid
     * pvduy 19/01/2021
     */
    alwayTakeColumns: {
      default: null,
      type: Array,
    },
    /**
     * có filter cột không
     */
    filterable: {
      type: Boolean,
      default: true,
    },
    /**
     * Cho phép sắp xếp lại dữ liệu
     */
    dataDragable: {
      default: false,
      type: Boolean,
    },
    /*
     * pvduy 19/03/2021
     * cờ xác nhận xem có cho hiện ảnh trống khi grid ko có dữ liệu hay không? ==> sử dụng trong grid detail dù trống vẫn ẩn ảnh đi.
     */
    isShowImageGridEmpty: {
      default: true,
      type: Boolean,
    },

    //Có footer hay không ?
    footer: {
      default: false,
      type: Boolean,
    },
    /*
     * nnlam 14/04/2021
     * có cache cấu hình cột không, hiện tại nếu lưu cache sẽ lưu khi resize cột
     */
    hasCacheCol: {
      default: false,
      type: Boolean,
    },
    /*
     * nnlam 14/04/2021
     * khi queryMode = 'local' thì dùng cái này để filter
     */
    filterFn: {
      default: false,
      type: [Boolean, Function],
    },
    /*
     * nnlam 14/04/2021
     * cho phép check disable ô checkbox ở row
     */
    disabledCheckedMultipleTbody: {
      default: false,
      type: [Boolean, Function],
    },

    //module để thực hiện autocomplete khi lọc trên th
    moduleAutocomplete: {
      type: String,
      default: null,
    },
    /**
     * Dữ liệu tổng
     * Dùng cho sum lấy từ server truyền vào (không phụ thuộc vào trang đang hiển thị)
     */
    summaryData: {
      type: Object,
      default: null,
    },
    /**
     * Dữ liệu tổng
     * Dùng cho sum lấy từ server truyền vào (không phụ thuộc vào trang đang hiển thị)
     */
    isAutoFocusFirstRow: {
      type: Boolean,
      default: true,
    },

    /**
     * Thuộc tính dùng nhưng chưa được định nghĩa => thêm vào tuy nhiên chưa rõ cụ thể làm gì
     * HTHIEP 21.03.2022
     */
    _events: null,

    /**
    Text tìm kiếm
     */
    searchText: {
      type: String,
      default: () => '',
    },

    /**
     * Text tìm kiếm remote
     */
    searchTextRemote: {
      type: String,
      default: '',
    },

    /**
     * Custom thêm lọc grid
     **/
    customFilter: {
      default: null,
      type: [Function, Array],
    },

    /**
     * Các trường tìm kiếm với độ ưu tiên
     * Format: [{ field: 'code', priority: 1 }, { field: 'name', priority: 2 }]
     */
    searchFields: {
      type: Array,
      default: () => [],
    },

    /**
     * Bật/tắt chế độ tìm kiếm ưu tiên
     */
    searchPriorityMode: {
      type: Boolean,
      default: true,
    },

    /**
     * Tự động focus vào kết quả tìm kiếm tốt nhất
     */
    autoFocusOnSearch: {
      type: Boolean,
      default: true,
    },

    /**
     * tên của màn hình danh sách
     */
    entityName: {
      type: String,
      default: 'dữ liệu',
    },

    /**
     * loại của empty state
     */
    variantEmptyState: {
      type: String,
      default: 'default',
    },

    /**
     * danh sách các nút hành động chính trong empty state
     * Mỗi nút có cấu trúc { name: 'Thêm', key: 'add' }
     */
    primaryActions: {
      type: Array,
      default: () => [],
    },

    /**
     * Hiển thị border trái và phải cố định cho grid
     */
    showBorder: {
      type: Boolean,
      default: true,
    },
    /**
     * có hiển thị container empty hay không
     */
    canShowContainerEmpty: {
      default: true,
      type: Boolean,
    },
  },
  data() {
    return {
      //Data trong grid dùng để render
      datax: null,
      //Columns trong grid dùng để render
      columnx: [],

      // Mode cho phép copy dữ liệu hay không thực hiện với các event kéo thả cột,độ rộng,...
      selectedTextMode: true,

      //Trang hiện tại
      currentPage: 1,

      //Dòng đang được chọn
      rowSelected: {},
      //Kiểm tra data đã được load hay chưa
      dataLoaded: false,
      /**
       * Vị trí hiển thị của box empty
       */
      emptyStyle: {
        top: 0,
        left: 0,
        display: 'none',
      },
      /**
       * Filter trên tiêu đề cột
       */
      filterHeader: [],
      /**
       * header render
       */
      headers: [],
      headerDrag: [],
      /*
      Cho show tất cả icon filter header lên
      Create: 20/05/2020
      Author: NNLAM    
      */
      showAllIconFilter: false,

      /**
       * footer render
       */
      footers: [],

      /**
       * header render
       */
      firstLoad: true,

      //tổng số bản ghi ( trường hợp query local)
      totalCount: null,
      /**
       * giá trị style left của cột serial
       */
      serialOffsetLeft: '0',
      /**
       * Có cột lock
       */
      hasLockColumn: false,
      /**
       * Có đang sử dụng phím tắt để chọn nhanh các bản ghi hay không
       */
      shortKeyForQuickSelect: false,
      /**
       * Những bản ghi dc chọn nhanh từ shortkey
       */
      shortkeyQuickSelectedData: [],
      pageSizeInternal: 0,
      /**
       * có scroll ngang không
       */
      hasScrollX: false,
      /**
       * có scroll dọc không
       */
      hasScrollY: false,
      /**
       * Check xem có đã check all hay ko
       */
      checkedAll: false,
      /**
       * Trạng thái indeterminate của header checkbox
       */
      headerIndeterminate: false,
      /**
       * Cho phép người dùng tùy chỉnh cột lock
       * Mặc định là false
       */
      lockable: {
        type: Boolean,
        default: false,
      },
    };
  },
  computed: {
    /**
     * Số lượng cột shimmer
     */
    shimmerColumn() {
      const me = this;
      let res = [];
      if (me.multiple) {
        res.push({
          offset: 0,
          lock: me.hasLockColumn,
        });
      }

      if (me.serial) {
        res.push({
          offset: me.multiple ? 60 : 0,
          lock: me.hasLockColumn,
        });
      }

      if (Array.isArray(me.columnx)) {
        me.columnx.forEach(item => {
          res.push({
            offset: item.stickyOffset,
            lock: item.lock,
          });
        });
      }

      if (me.$slots['widget-body']) {
        res.push({
          offset: 0,
          lock: me.hasLockColumn,
        });
      }

      return res;
    },
    //Kiểm tra có lắng nghe xử lý sự kiện dblick hay không? nếu có thì đổi icon thành pointer
    checkListenerDbClick() {
      if (this._events && this._events.dblclick && this._events.dblclick.length > 0) {
        return true;
      }
      return false;
    },
    //Sự kiện từ 1 dòng dữ liệu emit ra ngoài
    //Created by LTDAT(18.06.2020)
    listeners() {
      const me = this;
      return {
        drilldownClick: (metaData, event) => {
          if (event) {
            event.target.focus();
          }
          this.$emit('drilldownClick', metaData);
        },

        //TVLOI 22.05.2021 Event Click vào cộtđại diện cho nhiều dòng chi tiết
        viewMoreDetails: (metaData, event) => {
          if (event) {
            event.target.focus();
          }
          this.$emit('viewMoreDetails', metaData);
        },

        // dtnga3 (14.07.2022) Hiển thị form chi tiết khi click vào ô (nếu có config linkedForm)
        viewLinkedForm: (col, dataRow, event) => {
          if (event) {
            event.target.focus();
          }
          this.$emit('viewLinkedForm', col, dataRow);
        },

        mouseover: (dataRow, event) => {
          me.$emit('rowmouseover', dataRow, event);
        },
        rowactionclick: (command, dataRow, event) => {
          me.$emit('rowactionclick', me, command, dataRow, event);
        },

        keydown: (dataRow, event) => {
          me.row_keydown(dataRow, event);
          me.$emit('keydown', dataRow, event);
        },
        clickMultiple: (dataRow, checked) => {
          me.clickMultiple(dataRow, checked);
        },
      };
    },
    // //Set style cho grid độ rộng,...
    gridViewStyle() {
      if (this.maxHeight) {
        return {'max-height': this.maxHeight};
      } else {
        return null;
      }
    },
    dataEmpty() {
      // return false;
      if (this.datax && this.datax.length > 0) {
        return false;
      } else return true;
    },

    lockCount() {
      let count = this.multiple ? 1 : 0;
      if (this.columnx) {
        for (let i = 0; i < this.columnx.length; i++) {
          let col = this.columnx[i];
          if (!col.lock) {
            break;
          }
          count++;
        }
      }
      return count;
    },
    stickeyCountClass() {
      let count = this.lockCount;
      if (count > 0) {
        if (this.serial) {
          count++;
        }

        return ['sticky', count].join('-');
      }
      return null;
    },
    //Style tính lại tọa độ cho footer khi có pagination
    bottomFooter() {
      const me = this;
      if (me.pagination && me.footer) {
        return {
          bottom: '46px',
        };
      } else {
        return {
          bottom: '0px',
        };
      }
    },

    /**
     * Lấy searchFields đã được normalize
     */
    normalizedSearchFields() {
      const me = this;

      if (me.searchFields.length > 0) {
        return me.searchFields;
      }

      // Tự động tạo searchFields từ columns nếu không được chỉ định
      var result = me.columnx
        .filter(col => col.searchable !== false)
        .map((col, index) => ({
          field: col.dataField,
          priority: col.searchPriority || index + 1,
        }))
        .sort((a, b) => a.priority - b.priority);
      return result;
    },

    /**
     * Tính toán style cho border để chỉ hiển thị trong phạm vi table
     */
    borderStyle() {
      if (!this.showBorder) return {};

      const me = this;
      let paginationHeight = 0;
      let footerHeight = 0;
      let scrollbarWidth = 0;

      // Tính chiều cao pagination nếu có
      if (me.pagination) {
        paginationHeight = 50; // Chiều cao của pagination
      }

      // Tính chiều cao footer nếu có
      if (me.footer) {
        footerHeight = 28; // Chiều cao của footer
      }

      // Tính độ rộng scrollbar nếu có scroll dọc
      if (me.hasScrollY) {
        scrollbarWidth = 16; // Độ rộng chuẩn của scrollbar
      }

      return {
        '--border-bottom-offset': `${paginationHeight + footerHeight}px`,
        '--border-right-offset': `${scrollbarWidth}px`,
      };
    },
  },

  /**
   * Watcher để theo dõi rowSelected và đảm bảo grid luôn có thể nhận focus
   */
  watch: {
    rowSelected: {
      handler(newVal, oldVal) {
        // Khi rowSelected thay đổi, đảm bảo grid có thể nhận focus
        if (newVal && newVal.data && this.isAutoFocusFirstRow) {
          this.$nextTick(() => {
            this.ensureGridFocus();
          });
        }else{
          this.rowSelected={}
        }
      },
      deep: true,
      immediate: false,
    },
  },

  /**
   *
   */
  watch: {
    data: {
      immediate: true,
      handler(newVal, oldVal) {
        let me = this;
        this.checkHasScrollY();
        if (me.datax != me.data) {
          me.initData(newVal);
          if (me.firstLoad && newVal.length > 0) {
            me.autoSelectedFirstRow(window.event);
            me.firstLoad = false;
          } else if (newVal.length > 0 && me.isAutoFocusFirstRow) {
            // Tự động focus vào dòng đầu tiên khi data thay đổi (không phải lần đầu)
            me.$nextTick(() => {
              me.autoSelectedFirstRow();
            });
          }
          if (me.groupField) {
            // me.$nextTick(() => {
            //   me.defaultExpand();
            // });
          }
          // check selected các bản ghi khi mà load lại trang hoặc search làm thay đổi data grid
          if (Array.isArray(me.selected) && me.selected.length > 0) {
            me.checkedAll = me.isCheckedMultiple(me.selected);
          }
        }

        //nếu không có dữ liệu tính toán vị trí hiển thị của box empty
        //control chưa render thì cũng k xử lý
        if ((!newVal || newVal.length === 0) && me.$el) {
          me.$nextTick(() => {
            // let scroll = me.$refs.content;
            // if (scroll) {
            //   let scrollPos = scroll.getBoundingClientRect();
            //   me.emptyStyle = {
            //     top: [scrollPos.top + scrollPos.height / 2 - 95, 'px'].join(''),
            //     left: [scrollPos.left + scrollPos.width / 2 - 125, 'px'].join(
            //       ''
            //     ),
            //   };
            // }
          });
        }
      },
    },
    searchText: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.triggerSearch();
        }
      },
    },
    pageSize: {
      immediate: true,
      handler(newVal, oldVal) {
        // this.$emit("pageSize", newVal);
        this.pageSizeInternal = newVal;
      },
    },
    pageTotal: {
      immediate: true,
      handler(newVal, oldVal) {
        this.totalCount = newVal;
      },
    },
    selected: {
      handler(newVal, oldVal) {
        //Do checkedAll là v-model của ô checkbox nên phải đợi khi flush hết job thay đổi của ô khi click mới thực hiện check lại xem đã check hết bản ghi trên grid chưa
        //NCPHUOC 11/07/2022
        nextTick(() => {
          setTimeout(() => {
            this.checkedAll = this.isCheckedMultiple(newVal);
            this.updateHeaderIndeterminateState();
          }, 0);
        });
      },
    },
  },
  /**
   * Hàm khởi tạo grid khởi tạo column vào data
   * Created by LTDAT 16.06.2020
   */
  created() {
    const me = this;
    me.initColumns();
    me.initData();
    document.addEventListener('click', me.clickOutGrid);
    window.addEventListener('resize', me.windowResize);

    // Lắng nghe khi window được focus lại (sau khi đóng form)
    window.addEventListener('focus', me.handleWindowFocus);
  },
  /**
   * Setup hook
   */
  setup(props, {emit}) {
    const {changeColumnResize, handlerResizeOn} = useColumnResize({
      props,
      emit,
    });
    onMounted(() => {
      nextTick(() => {
        setTimeout(() => {
          proxy.updateFooterContainer();

          // Đảm bảo grid có thể nhận focus sau khi mount
          if (proxy.isAutoFocusFirstRow && proxy.datax && proxy.datax.length > 0) {
            proxy.autoSelectedFirstRow();
          }else{
            proxy.rowSelected={}
          }
        }, 300);
      });
    });
    const {proxy} = getCurrentInstance();

    /**
     * Xử lý ẩn hiện dạng cây group
     * NCPHUOC 27/05/2022
     */
    const toggleTree = dataRow => {
      const data = proxy.groupData.find(x => x === dataRow);
      if (data) {
        data.expanded = !data.expanded;
        changeVisibleTr(data, data.expanded);
      }
    };

    /**
     * Đệ quy lấy các node con để ẩn hiện
     */
    const changeVisibleTr = (data, expanded) => {
      let childNodes = data.childNodes;
      if (!childNodes || childNodes.length === 0) {
        return;
      }
      // data.expanded = expanded;
      childNodes.forEach(item => {
        item.isHide = !expanded;
        changeVisibleTr(item, expanded);
      });
    };
    return {changeColumnResize, handlerResizeOn, toggleTree};
  },
  methods: {
    onFocusRow(dataRow, event) {
      const me = this;
      if (me.rowSelected && me.rowSelected.data != dataRow && !me.shortKeyForQuickSelect) {
        let indexRow = me.datax.findIndex(item => item == dataRow);
        me.rowSelected = {
          data: dataRow,
          index: indexRow,
        };
        event.target.focus();
      }
    },
    onClickRow(dataRow, event) {
      const me = this;
      if (event && !me.shortKeyForQuickSelect) {
        me.oldIndexOfRowFocused = me.indexOfRowFocused;
        me.indexOfRowFocused = me.datax.findIndex(item => item == dataRow);
        event.target.focus();
        if (me.multiple && !event.target.closest('.ms-td.multiple-cell')) {
          this.$emit('onClickRowCustom', dataRow, event);
        }
      }
      if (!me.multiple) {
        //me.selected = dataRow;
        me.rowSelected.data = dataRow;
        me.rowSelected.index = me.datax.findIndex(item => item == dataRow);
        this.$emit('onClickRowCustom', dataRow, event);
      }

      // ✅ Đồng bộ selected state cho row span group
      me.$nextTick(() => {
        me.syncRowSpanGroupSelectedState();
      });
    },
    /**
     * Kiểm tra xem tất cả dữ liệu có đang được check hay không
     * created by LTDAT 25.06.2020
     * modify by nnlam 05/05/2021
     */
    isCheckedMultiple(selected) {
      const me = this;
      if (selected) {
        let data = this.datax.filter(i => !i.isDisabledCheckBox); //tìm tất cả bản ghi trên page mà ô checkbox k bị disabled
        //Nếu ko có paging thì check luôn length để kiểm tra selected hết
        if (!me.pagination) return selected.length === data.length;
        //ĐVThi 19/01/2021: sửa lỗi TA khi me.datax null thì đoạn code này văng lỗi TA

        //Nếu có rowspangroup thì thực hiện lọc ra các dòng master ở đầu
        if (this.rowSpanGroup) {
          data = this.groupDataRowSpan.filter(x => x[`rowspan-${this.groupField[0]}`]);
        }
        if (data && data.length != 0) {
          return (
            selected.length >= data.length &&
            data.every((item, index) => {
              return selected.findIndex(dataRow => dataRow[me.idField] === item[me.idField]) === -1 ? false : true;
            })
          );
        } else {
          return false;
        }
      }
    },
    /**
     * Tính toán xem có scroll ngang
     */
    updateHasScrollX() {
      const me = this,
        content = me.$refs.content;
      let scroll = false;
      if (content && content.clientWidth < content.firstElementChild.clientWidth) {
        scroll = true;
      }

      me.hasScrollX = scroll;
    },

    /**
     * Chuyển về trang chỉ định
     */
    setPage(page) {
      this.currentPage = page;
    },
    /**
     * Hiển thị text tính tổng theo định dạng format
     * @param {Object} col cấu hình cột
     * @author NVLAM 10.12.2020
     */
    getSummaryDisplay(col, rowIndex, colIndex) {
      const me = this;
      let dataRender = '';
      if (me.summaryData) {
        dataRender = me.summaryData[col.dataField];
      } else {
        dataRender = col.summaryType
          ? summaryUtil.doFunction(
              me.datax,
              col.summaryType,
              col.dataField,
              col.dataFormat,
              this.totalCount || this.datax.length,
            )
          : col.footerText;
      }
      //Return ngay nếu không cấu hình summary hoặc ko có dữ liệu
      if (!dataRender && col.summaryType && me.summaryData) return '';
      //Mặc định luôn không format cột đầu tiên do cột
      if (colIndex === 0) return dataRender;
      let value = me.$ms.format.formatData(dataRender, {
        formatType: col.dataFormat || col.formatType,
        optionFormat: col.optionFormat,
      });
      return value == '--' || value == '-' ? null : value;
    },
    /**
     * Hàm chọn dòng đầu tiên
     * ischeckbox: có tích chọn vào ô checkbox không, mặc định là không và chỉ đơn thuần là click vào dòng đầu tiên
     * created by nnlam 06.03.2021
     */
    autoSelectedFirstRow(ischeckbox) {
      let me = this;
      me.$nextTick(() => {
        if (me.datax.length > 0) {
          if (ischeckbox) {
            me.indexOfRowFocused = 0;
            me.rowSelected = {data: me.datax[0], index: 0};
          }
          setTimeout(() => {
            let trFirst = me.$el.querySelector('.ms-tbody.data tr');
            if (trFirst && me.isAutoFocusFirstRow) {
              trFirst.focus();
            }
            me.$emit('firstLoad', me.datax[0]);
            
            // ✅ Đồng bộ selected state cho row span group
            me.$nextTick(() => {
              me.syncRowSpanGroupSelectedState();
            });
          }, 200);
        }
      });
    },
    /**
     * Sự kiện window resize
     */
    windowResize() {
      this.updateSize();
    },
    /**
     * Cập nhật footer container with
     * phải đợi data render xong để xem có scroll dọc không còn update lại width
     * NXCHIEN 22/06/2022: Check width != 0 cho TH grid đang bị ẩn
     */
    updateFooterContainer() {
      const me = this,
        width = me.$refs.content?.clientWidth;
      if (width && me.$refs.tableFooter) {
        me.$refs.tableFooter.style.width = width + 'px';
      }
    },
    /**
     * Hàm Cập nhật lại giao diện khi có scroll hay khi độ rộng của data dài hơn độ rộng mặc định của cột
     * Created by LTDAT
     */
    updateSize() {
      const me = this;
      me.$nextTick(() => {
        // me.checkHasScrollY();
        if (me.$refs.tr && me.$refs.tr.length > 0) {
          me.columnx.forEach((col, index) => {
            let changeMinWidth = false;
            me.$refs.tr.forEach(item => {
              if (item.$refs[`text-${col.dataField}`] && item.$refs[`text-${col.dataField}`].length > 0) {
                let textSpan = item.$refs[`text-${col.dataField}`][0].offsetWidth;
                if (col._width < textSpan + 20) {
                  changeMinWidth = true;
                  if (me.$refs.th[index].$el.offsetWidth != item.$refs[`td-${col.dataField}`][0].offsetWidth) {
                    col['_minWidth'] = item.$refs[`td-${col.dataField}`][0].offsetWidth;
                    col['_width'] = item.$refs[`td-${col.dataField}`][0].offsetWidth;
                  }
                }
              }
            });
            if (changeMinWidth == false) {
              col['_minWidth'] = col._width;
            }
          });
          if (me.$refs.trGroup) {
            me.$refs.trGroup.forEach((item, index) => {
              let tdGroup = item.querySelector('.td-group');
              if (tdGroup) {
                let colspan = tdGroup.getAttribute('colspan');
                let tdGroupWidth = 0;
                if (me.$refs.th && colspan) {
                  let i = me.multiple ? 0 : 1;
                  for (i; i <= colspan; i++) {
                    let thCurrentElement = me.$refs.th[i];
                    if (thCurrentElement) {
                      tdGroupWidth += thCurrentElement.$el.offsetWidth;
                    }
                  }
                }
                tdGroup.style.width = tdGroupWidth + 'px';
                tdGroup.style.maxWidth = tdGroupWidth + 'px';
              }
            });
          }
        }

        //tính toán xem có scroll ngang không
        me.updateHasScrollX();

        //cập nhật footer
        if (me.footer) {
          me.initFooterColumn(me.columnx);
          this.updateFooterContainer();
        }
      });
    },
    // /**
    //  * Hàm trước thực hiện transition bắt đầu render.
    //  * Created by LTDAT
    //  */
    // beforeRenderContent() {
    //   const me = this;
    //   me.updateSize();
    // },
    /**
     * Thực hiện khi ấn vào ngoài grid loại bỏ row đang chọn
     * Created by LTDAT
     *  */
    clickOutGrid(event) {
      const me = this;
      if (event.target.closest('.ms-grid-viewer') != me.$el) {
        //me.rowSelected = {};  nnlam bỏ đi vì khi click ra ngoài thì những dòng được chọn mong muốn vẫn giữ nguyên focus
      }
    },
    /**
     * Hàm thực hiện việc sort trên grid
     * @params col cột được thực hiện sắp sếp
     * @params mode gồm null,DESC,ASC
     * created by LTDAT
     */
    sort(col, mode) {
      const me = this,
        pageSize = me.getPageSize();

      if (me.sortMode == 'remote') {
        // thực hiện bỏ sort tất cả các cột khác
        me.headers[0]?.forEach((item, index) => {
          if (item.dataField != col.dataField) {
            item.sortStatus = 0;
          } else {
            item.sortStatus = mode;
          }
        });
        let payload = {
          skip: (me.currentPage - 1) * pageSize,
          take: pageSize,
        };
        if (mode != me.$ms.constant.SortStatus.none) {
          payload.sort = [col.dataField, mode == me.$ms.constant.SortStatus.desc ? 'DESC' : 'ASC'].join(' ');
        }
        me.listenersLoadData(payload);
      } else if (me.datax && me.datax.length > 0) {
        let data = null;
        if (me.queryMode == 'local' && typeof me.filterFn == 'function') {
          data = me.filterFn();
          me.totalCount = data ? data.length : 0;
        } else {
          data = [...me.internalDataSource];
        }
        //hàm sort
        function compare(a, b) {
          if (a[col.dataField] < b[col.dataField]) return mode == me.$ms.constant.SortStatus.desc ? 1 : -1;
          if (a[col.dataField] > b[col.dataField]) return mode == me.$ms.constant.SortStatus.asc ? 1 : -1;
          return 0;
        }

        //thực hiện sort
        if (mode != me.$ms.constant.SortStatus.none) {
          data = data.sort(compare);
        }

        //nếu có phân trang thì phân trang
        if (me.pagination) {
          let min = Math.ceil((me.currentPage - 1) * pageSize),
            max = min + pageSize;
          me.datax = data.filter((item, index) => {
            return index >= min && index < max;
          });
        } else {
          me.datax = data;
        }
      }
    },
    getPageSize() {
      return this.pageSizeInternal || 10;
    },
    //Kiểm tra xem grid có scroll hay không để chỉnh lại size của grid cho đúng
    //Created by LTDAT (29.07.2020)
    checkHasScrollY() {
      const me = this;
      me.$nextTick(() => {
        let content = me.$refs.content;
        if (content) {
          setTimeout(() => {
            let height = content.clientHeight;
            if (me.pagination) {
              height -= 28;
            }
            if (me.footer) {
              height -= 28;
            }
            if (height < content.firstElementChild.clientHeight) {
              me.hasScrollY = true;
            } else {
              me.hasScrollY = false;
            }
            let x = 1;
          }, 100);
        }
      });
    },

    /**
     * Update sticky top cho grid dạng group
     * HTHIEP 19.04.2022
     */
    updateStickyTopGroup() {
      if (this.headers?.length > 1) {
        //lấy thông tin tr cuối cùng
        const lastItems = this.headers[this.headers?.length - 1];
        if (lastItems?.length > 0) {
          //lấy thông tin height của 1 thẻ tr dùng để làm căn cứ set top
          const trHeight = this.$el.querySelector('.ms-tr')?.getBoundingClientRect()?.height;

          //Lấy ra cột group để tiến hành set top
          const groupItems = lastItems.filter(x => x.group);
          groupItems.forEach(item => {
            const th = this.$el.querySelector(`.ms-th[field="${item.dataField}"]`);
            if (th) {
              const le = this.headers?.length - 1;
              th.style.top = `${trHeight * (this.headers?.length - 1)}px`;
            }
          });
        }
      }
    },

    /**
     * Hàm lắng nghe sự kiến thay đổi trang nếu thay đổi thì emit sự kiện load data mới ra ngoài
     * @param payload dữ liệu thông tin trang gửi ra ngoài
     *  */
    //Created by LTDAT(27.07.2020)
    listenersLoadData(payload) {
      const me = this;
      if (me.pagination) {
        me.currentPage = payload.skip / payload.take + 1;
        //Nếu thực hiện lấy data dưới local thì thức hiện lưu dữ liệu vào internalDataSource và phân trang local
        if (me.queryMode == 'local' && me.pagination) {
          // commonFn.genVKeyValue(me.internalDataSource);
          me.datax = me.getItemPagination();
        }
      }
      me.$refs.content.scrollTop = 0;
      me.$emit('loadData', payload);
    },

    /**
     * Kiểm tra xem cột được check hay không ?
     * duyệt trong data nếu có chưa dữ liệu của dòng tương ứng thì return true ngược lại false
     * @param dataRow dữ liệu của dòng được check
     * Created by LTDAT 24.06.2020
     *  */
    isSelected(dataRow) {
      const me = this;
      if (me.selected && me.selected.length > 0) {
        return this.selected.findIndex(x => x[me.idField] === dataRow[me.idField]) === -1 ? false : true;
      }
      return false;
    },
    /**
     * Kiểm tra xem row có disable checkbox k
     * @param dataRow dữ liệu của dòng được check
     * Created by nnlam 24.06.2020
     *  */
    funDisabledCheckedMultipleTbody(dataRow) {
      let me = this;
      if (typeof me.disabledCheckedMultipleTbody == 'function') {
        return me.disabledCheckedMultipleTbody(dataRow);
      }
      return me.disabledCheckedMultipleTbody;
    },
    /**
     * Hàm xử lý sự kiện khi click vào ô check multiple
     * @param dataRow dữ liệu của dòng được chọn
     * Created by LTDAT (30.05.2020)
     */
    clickMultiple(dataRow, checked) {
      const me = this;

      let VModel = me.selected ? [...me.selected] : [];

       if (me.rowSpanGroup && me.groupField && me.groupField.length > 0) {
        const groupValue = dataRow[me.idField];
        // Tìm tất cả items có cùng groupField value
        let itemsToProcess = me.datax.filter(
          (item) => item[me.idField] === groupValue
        );

        VModel = VModel.filter((i) => i[me.idField] !== groupValue);

        if (checked) {
          VModel.unshift(...itemsToProcess);
        }
       }else{
        const index = VModel.findIndex(x => x[me.idField] === dataRow[me.idField]);
        // Nếu không truyền checked, thực hiện toggle
        if (checked === undefined) {
          if (index === -1) {
            // Chưa được check, thêm vào
            VModel.push(dataRow);
            checked = true;
          } else {
            // Đã được check, bỏ check
            VModel = VModel.filter(x => x[me.idField] !== dataRow[me.idField]);
            checked = false;
          }
        } else {
          // Nếu truyền checked, thực hiện theo giá trị
          if (!checked) {
            VModel = VModel.filter(x => x[me.idField] !== dataRow[me.idField]);
          } else {
            if (index === -1) {
              VModel.push(dataRow);
            }
          }
        }
       }

      // ✅ NEW: Active row cuối cùng được check
      if (checked) {
        // ✅ NEW: Active row cuối cùng được check
        me.rowSelected = {
          data: dataRow,
          index: me.datax.findIndex(item => item === dataRow),
        };
      }
      //Đoạn này ko cần nữa vì đã watch selected rồi
      // const isCheckedAll = me.isCheckedMultiple(VModel);
      // me.checkedAll = isCheckedAll;
      me.$emit('update:selected', VModel);
    },
    /**
     * Hàm xử lý sự kiện khi click vào ô check multiple dùng phím tắt
     * @param dataRow dữ liệu của dòng được chọn
     * Created by NNLAM (30.05.2020)
     */
    clickMultipleByShorKey(list) {},

    //Lấy dữ liệu của 1 trang khi phân trang local
    //Created by LTDAT(18.06.2020)
    getItemPagination() {
      let me = this,
        pageSize = me.getPageSize(),
        min = Math.ceil((me.currentPage - 1) * pageSize),
        max = min + pageSize;

      let data = null;
      if (me.queryMode !== 'remote') {
        data = me.localFilterData;
      } else {
        data = me.internalDataSource;
      }

      data = data || [];
      let dataPagination = data.filter((item, index) => {
        return index >= min && index < max;
      });
      return dataPagination;
    },

    //Xử lý sự kiện resize col trên grid
    //created by LTDAT(29.05.2020)
    //cmt by: nnlam 24/02/2021: bỏ đi vì thừa gây xấu khi mà click vào resize thì làm change width toàn bộ column luôn
    resizeActive() {
      //Không cho phép bôi đen text
      // this.selectedTextMode = true;
      // if (this.$refs.th) {
      //   let listTh = this.$refs.th;
      //   if (listTh && listTh.length > 0) {
      //     listTh.forEach((item) => {
      //       if (item == listTh[listTh.length - this.indexFlexRight]) {
      //         item.updateColWidth(true);
      //       } else {
      //         item.updateColWidth(false);
      //       }
      //     });
      //   }
      // }
    },
    //Đóng sự kiện resize cột trên grid
    //created by LTDAT(29.05.2020)

    resizeClose(col) {
      const me = this;
      //Cho phép bôi đên text
      me.selectedTextMode = false;
      col.newWidth = col._width;
      me.$emit('resizeCol', col);

      //tính toán xem có scroll ngang không
      me.updateHasScrollX();

      //cập nhật footer
      if (me.footer) {
        me.initFooterColumn(me.columnx);
        me.updateFooterContainer();
      }
    },
    /**
     * Set giá trị default cho cấu hình cột
     * Created by LTDAT(17.07.2020)
     */
    setDefaultFieldColumn(columns) {
      //Nếu không cấu hình field hiện thị thì mặc định là true
      const me = this;
      columns.forEach(item => {
        //món này là lock range nên sẽ không đặt cấu hình từng cột mà ăn chung theo grid
        item.lockable = me.lockable;

        if (item.visible != false) {
          item.visible = true;
        }

        //gắn giá trị columnType theo formatType
        if (!item.columnType) {
          me.setDefaultColumnType(item);
        }

        //nếu không chỉnh định thì để align mặc dịnh theo format type
        if (!item.align) {
          switch (item.columnType) {
            case 'ColumnNumber':
              item.align = 'right';
              break;
            case 'ColumnDateTime':
              item.align = 'left';
            case 'ColumnBoolean':
              item.align = 'center';
              break;
          }
        }
        item['_width'] = item.width;
      });
    },
    /** Lấy ra các column có thuộc tính visible = true
     * Created by LTDAT 17.07.2020
     * */
    getVisibleColumn(columns) {
      return columns.filter(item => {
        return item.visible == true;
      });
    },
    /**
     * Khởi tạo dữ liệu ban đầu cho grid
     * columns truyền vào mặc định là columns trong props có thể truyền cấu hình khác từ ngoài vào
     */
    initColumns(columns) {
      const me = this;
      me.columnx = [];
      me.$nextTick(() => {
        me.columnx = columns || me.columns || [];
        me.setDefaultFieldColumn(me.columnx);
        me.columnx = me.getVisibleColumn(me.columnx);
        if (!me.columnx) {
          me.columnx = [];
        }
        let hasFlex = me.columnx.some(item => {
          return item.autoResize;
        });
        if (hasFlex == false && me.columnx[me.columnx.length - me.indexFlexRight]) {
          // me.resizeActive();
          if (me.columnx && me.columnx[me.columnx.length - me.indexFlexRight]) {
            me.columnx[me.columnx.length - me.indexFlexRight]['autoResize'] = true;
          }
        } else if (hasFlex == false && me.columnx.length == 1) {
          me.columnx[0]['autoResize'] = true;
        }

        //xử lý group header
        me.initHeader(me.columnx);

        //gán stickty left cho các cột sticky
        me.$nextTick(() => {
          //tính toán xem có scroll ngang không
          me.$nextTick(() => {
            me.updateHasScrollX();
          });

          me.updateStickyOffset();

          //tính toán xem có scroll ngang không
          me.updateHasScrollX();

          //nếu có footer thì sử lý
          if (me.footer) {
            setTimeout(() => {
              me.initFooterColumn(me.columnx);
              // me.updateFooterContainer();
            }, 300);
          }

          //HTHIEP 19.04.2022: Thêm xử lý sticky col cho group
          me.$nextTick(() => {
            this.updateStickyTopGroup();
          });
        });
      });
    },
    /**
     * Xử lý tiêu đề cột
     */
    initHeader(columns) {
      const me = this,
        headers = [],
        sub = [];
      let group;
      columns = columns?.filter(x => x.visible != false);
      columns.forEach((item, i) => {
        if (item.group) {
          if (!group || group.caption !== item.group) {
            group = {
              isGroup: true,
              colspan: 1,
              caption: item.group,
              lockable: me.lockable,
              lock: false,
              childs: [],
            };
            headers.push(group);
          } else {
            group.colspan++;
          }
          group.childs.push(item);
          sub.push(item);
        } else {
          headers.push(item);
        }
      });

      columns.forEach((x, index, arr) => {
        if (x.hasOwnProperty('last')) {
          delete x.last;
        }

        if (arr.length - 1 === index) {
          x.last = true;
        }
      });

      //duyệt xử lý các nhóm cột chỉ có 1 phần từ -> bỏ nhóm đi
      if (me.ignoreGroup) {
        for (let i = 0; i < headers.length; i++) {
          let col = headers[i];
          if (col.childs) {
            if (col.childs.length === 1) {
              let child = col.childs[0];
              headers[i] = child;
              sub.remove(child);
            }
            //delete col.childs;
          }
        }
      }
      // buid subheader nhiều cấp
      function addSubHeader(header, grade) {
        if (me.headers[grade] && Array.isArray(me.headers[grade])) {
          me.headers[grade].push(header);
        } else {
          me.headers[grade] = [header];
        }
        if (header.childs && Array.isArray(header.childs)) {
          header.childs.forEach(item => addSubHeader(item, grade + (header.rowspan ?? 1)));
        }
      }
      me.headers = [];
      headers.forEach(x => addSubHeader(x, 0));

      me.headerDrag = me.headers[0];
    },

    /**
     * Xử lý tiêu đề cột
     * Xử lý trường hợp có summaryType ở footer thì forces ô tổng :NCPHUOC 15/07/2022
     */
    initFooterColumn(columns) {
      const me = this,
        footers = [],
        sub = [],
        thead = me.$refs.thead;

      let columx = JSON.parse(JSON.stringify(columns));
      let firstCol = null;
      if (columx.length > 0) {
        if (me.multiple || me.showTotalRecord) {
          columx.insertAt(0, {
            stickyOffset: 0,
            colspan: 1,
            width: columx[0].stickyOffset,
            // summaryType: me.$ms.constant.SummaryType.RowCount,
            dataField: 'multiple',
            classes: 'footer-total-summary',
          });

          firstCol = columx[1];
          if (!firstCol.summaryType) {
            firstCol.footerText = 'Tổng';
            firstCol.classes = 'footer-total-summary';
          }
        } else {
          if (me.footer && !columx[0].summaryType) {
            // columx[0].summaryType = me.$ms.constant.SummaryType.RowCount;
            columx[0].classes = 'footer-total-summary';
            columx[1].footerText = 'Tổng';
            columx[1].classes = 'footer-total-summary';
          }
        }
      }

      for (let i = 0; i < columx.length; i++) {
        let col = columx[i];
        //Nếu có multiple sẽ gộp 2 cột đầu tiên lại để hiển thị số tổng trên footer
        // Do cột đầu tiên là cột checkbox quá bé k hiển thị được
        // if (i === 1 && me.multiple) {
        //   continue;
        // }
        let item = {
          dataField: col.dataField,
          align: col.align,
          formatType: col.formatType,
          footerText: col.footerText,
          summaryType: col.summaryType,
          stickyOffset: col.stickyOffset,
          colspan: col.colspan || 1,
          width: col.width,
          classes: col.classes,
        };

        if (thead) {
          let th = thead.querySelector(['th[field="', col.dataField, '"]'].join(''));
          if (th) {
            item.width = th.getBoundingClientRect().width || col.width;
            if (me.lockCount > 0 && me.lockCount === i + 1) {
              item.width += 1; //+1 do border lock
            }
          }
        }
        footers.push(item);
      }

      //xử lý cột widget ở cuối
      if (me.$slots['widget-title-th'] || me.$slots['widget-title']) {
        let widget = thead?.querySelector('.widget-title');
        if (widget) {
          footers.push({
            dataField: 'widget',
            colspan: 1,
            width: widget.getBoundingClientRect().width,
            stickyRight: true,
          });
        }
      }
      me.footers = [footers];
    },
    /**
     * Cập nhật left cho các cột stickey
     */
    updateStickyOffset() {
      const me = this,
        ths = me.$el.querySelectorAll('.ms-th'),
        columns = me.columnx;
      let usingColumnSetting = false;

      let offset = 0;
      let f = 0;
      let width = 0;
      if (me.multiple) {
        width = ths[f].getBoundingClientRect().width || 0;
        // trường hợp grid bị ẩn đi -> offset của ô checkbox bị = 0; (lưu ý trên form có 2 grid trên 2 ms-tap-item khác nhau thì grid ở tab không actice sẽ bị lỗi left sticky)
        if (width === 0) {
          usingColumnSetting = true;
          width = 53;
        }
        offset += width;
        f++;
      }

      if (me.serial) {
        me.serialOffsetLeft = offset ? [offset, 'px'].join() : '0';

        width = ths[f].getBoundingClientRect().width || 0;
        // trường hợp grid bị ẩn đi -> offset của ô checkbox bị = 0; (lưu ý trên form có 2 grid trên 2 ms-tap-item khác nhau thì grid ở tab không actice sẽ bị lỗi left sticky)
        if (width === 0) {
          width = 60;
        }
        usingColumnSetting = true;
        offset += width;
        f++;
      }

      me.hasLockColumn = false;
      let i = 0;
      for (; i < me.columnx.length; i++) {
        let col = me.columnx[i];
        if (!col.lock) {
          break;
        }

        me.hasLockColumn = true;
        col['stickyOffset'] = offset;

        let pos = 0;
        if (!usingColumnSetting) {
          pos = ths[i + f].getBoundingClientRect();
        } else {
          pos = {width: columns[i + f - 1].width};
        }

        offset += pos.width;
      }

      //remove offset column not locked
      for (let j = i; j < me.columnx.length; j++) {
        let col = me.columnx[j];
        col['stickyOffset'] = null;
      }

      me.initFooterColumn(me.columnx);
    },
    //Xóa 1 phần tử trong data grid
    removeItem(item) {
      const me = this;
      me.datax = _.remove(me.datax, e => {
        return e !== item;
      });
    },
    //Gán giá trị để render data có thể truyền từ ngoài vào
    initData(data = this.data) {
      const me = this,
        updateScroll = !data != !me.datax || data.length != me.datax.length;
      me.internalDataSource = null;
      if (data) {
        me.internalDataSource = [...data];
      }
      //Nếu thực hiện lấy data dưới local thì thức hiện lưu dữ liệu vào internalDataSource và phân trang local
      if (me.queryMode !== 'remote' && me.pagination) {
        me.localFilter();
        // commonFn.genVKeyValue(me.internalDataSource);
        me.datax = me.getItemPagination();
      } else {
        //Lấy dữ liệu từ server
        //Tạm gán = props data sau gán = gì khác tính sau
        //ĐVThi 19/01/2021: sửa lỗi TA khi me.datax null thì đoạn code này văng lỗi TA
        if (me.internalDataSource) {
          me.datax = [...me.internalDataSource];
        }
        // commonFn.genVKeyValue(me.datax);
      }

      me._cacheData = [...me.datax];

      if (this.searchText || (this.filterObject && Object.keys(this.filterObject).length > 0)) {
        this.triggerSearch();
      }

      // Thực hiện tìm kiếm remote và focus vào kết quả
      me.$nextTick(() => {
        me.handleRemoteSearch();
      });

      if (updateScroll) {
        me.$nextTick(() => {
          this.checkHasScrollY();
        });
      }
    },

    /**
     * Xử lý tìm kiếm và focus vào kết quả khi searchTextRemote thay đổi
     */
    handleRemoteSearch() {
      const me = this;
      // Kiểm tra các điều kiện chung
      if (
        me.queryMode === 'remote' &&
        me.searchPriorityMode &&
        me.autoFocusOnSearch &&
        me.datax &&
        me.datax.length > 0
      ) {
        // Trường hợp có searchTextRemote - tìm theo ưu tiên
        if (me.searchTextRemote && me.normalizedSearchFields.length > 0) {
          // Tìm item phù hợp nhất
          const searchResult = me.smartSearch(me.datax, me.searchTextRemote, me.normalizedSearchFields);

          // Focus vào kết quả tốt nhất nếu tìm thấy
          if (searchResult.bestMatch) {
            me.focusToItem(searchResult.bestMatch);
          }
        }
        // Trường hợp không có searchTextRemote - focus vào dòng đầu tiên
        else if (!me.searchTextRemote) {
          // Focus vào dòng đầu tiên
          me.focusToItem(me.datax[0]);
        }
      }

      // Đảm bảo grid luôn có thể nhận focus sau khi search
      me.$nextTick(() => {
        me.ensureGridFocus();
      });
    },

    /**
     * Thực hiện search thông minh với độ ưu tiên
     */
    triggerSearch() {
      const me = this;
      if (!me.searchText && (!me.filterObject || Object.keys(me.filterObject).length === 0)) {
        me.datax = [...me._cacheData];
        me.totalCount = me.datax ? me.datax.length : 0;
        return;
      }

      let filteredData = me.data;

      // Áp dụng filterObject trước (nếu có)
      if (me.filterObject && Object.keys(me.filterObject).length > 0) {
        filteredData = filteredData.filter(x => commonFn.checkFilterObject(x, me.filterObject));
      }

      // Áp dụng search thông minh
      if (me.searchText && me.searchPriorityMode && me.normalizedSearchFields.length > 0) {
        const searchResult = me.smartSearch(filteredData, me.searchText, me.normalizedSearchFields);
        me.datax = searchResult.results;

        // Auto focus vào best match
        if (me.autoFocusOnSearch && searchResult.bestMatch) {
          me.$nextTick(() => {
            me.focusToItem(searchResult.bestMatch);
          });
        }
      } else if (me.searchText) {
        // Fallback về cơ chế cũ nếu không bật priority mode hoặc không có searchFields
        const properties = me.columnx.map(x => x.dataField);

        const data = [];
        //Cache lại những item đã được thêm vào để không cần duyệt mảng check
        const cacheExist = new Map();
        const idField = me.idField;
        const findParent = currentItem => {
          const isExist = cacheExist.get(currentItem[idField]);
          //Cache lại để đệ quy ko tìm vào item này nữa
          if (!isExist) {
            cacheExist.set(currentItem[idField], true);
          }

          if (currentItem.parentNode) {
            findParent(currentItem.parentNode);
          }
          //Thực hiện push ở sau để push các thằng cha vào trước
          if (!isExist) {
            data.push(currentItem);
          }
          return;
        };

        filteredData.forEach(x => {
          let valid = commonFn.checkSearchText(x, properties, me.searchText);
          if (valid) {
            findParent(x);
          }
        });

        me.datax = data;
      } else {
        me.datax = filteredData;
      }

      me.totalCount = me.datax ? me.datax.length : 0;

      // Xử lý parent-child relationship (giữ nguyên logic cũ nếu cần)
      me.handleParentChildRelationship();

      // Đảm bảo grid luôn có thể nhận focus sau khi filter
      me.$nextTick(() => {
        me.ensureGridFocus();
      });
    },

    /**
     * Tìm kiếm thông minh với độ ưu tiên
     * @param {Array} data - Dữ liệu cần tìm
     * @param {String} searchText - Text tìm kiếm
     * @param {Array} searchFields - Các trường tìm kiếm với độ ưu tiên
     * @returns {Object} { results: [], bestMatch: null }
     */
    smartSearch(data, searchText, searchFields) {
      const me = this;
      if (!searchText || !searchFields.length) return {results: data, bestMatch: null};

      const results = [];
      let bestMatch = null;
      let bestScore = Infinity;

      data.forEach(item => {
        const matchInfo = me.calculateMatchScore(item, searchText, searchFields);
        if (matchInfo.isMatch) {
          results.push(item);

          // Tìm best match
          if (matchInfo.score < bestScore) {
            bestScore = matchInfo.score;
            bestMatch = item;
          }
        }
      });

      return {results, bestMatch};
    },

    /**
     * Tính điểm ưu tiên cho từng item
     * @param {Object} item - Item cần kiểm tra
     * @param {String} searchText - Text tìm kiếm
     * @param {Array} searchFields - Các trường với độ ưu tiên
     * @returns {Object} { isMatch: boolean, score: number, matchedField: string }
     */
    calculateMatchScore(item, searchText, searchFields) {
      let bestScore = Infinity;
      let matchedField = null;
      let isMatch = false;

      const normalizedSearch = searchText.toLowerCase().trim();

      searchFields.forEach(fieldConfig => {
        const fieldValue = item[fieldConfig.field];
        if (!fieldValue) return;

        const normalizedValue = fieldValue.toString().toLowerCase();

        // Exact match (điểm thấp nhất = tốt nhất)
        if (normalizedValue === normalizedSearch) {
          const score = fieldConfig.priority * 10; // Exact match
          if (score < bestScore) {
            bestScore = score;
            matchedField = fieldConfig.field;
            isMatch = true;
          }
        }
        // Starts with
        else if (normalizedValue.startsWith(normalizedSearch)) {
          const score = fieldConfig.priority * 20; // Starts with
          if (score < bestScore) {
            bestScore = score;
            matchedField = fieldConfig.field;
            isMatch = true;
          }
        }
        // Contains
        else if (normalizedValue.includes(normalizedSearch)) {
          const score = fieldConfig.priority * 30; // Contains
          if (score < bestScore) {
            bestScore = score;
            matchedField = fieldConfig.field;
            isMatch = true;
          }
        }
      });

      return {isMatch, score: bestScore, matchedField};
    },

    /**
     * Focus đến một item cụ thể trong grid
     * @param {Object} targetItem - Item cần focus
     */
    focusToItem(targetItem) {
      const me = this;
      if (!targetItem || !me.idField) return;

      const index = me.datax.findIndex(item => item[me.idField] === targetItem[me.idField]);

      if (index !== -1) {
        // Cập nhật rowSelected
        me.rowSelected = {
          data: targetItem,
          index: index,
        };

        // Scroll đến vị trí của item
        me.$nextTick(() => {
          const tr = me.$el.querySelector(`.ms-tbody.data tr[indexRow='${index}']`);

          if (tr) {
            tr.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            // Focus vào tr
            setTimeout(() => {
              tr.focus();
              me.$emit('searchFocused', targetItem, index);
            }, 100);
          }
        });
      }
    },

    /**
     * Xử lý parent-child relationship (giữ nguyên logic cũ)
     */
    handleParentChildRelationship() {
      const me = this;
      if (!me.data.some(item => item.parentNode)) return;

      const data = [];
      const cacheExist = new Map();
      const idField = me.idField;

      const findParent = currentItem => {
        const isExist = cacheExist.get(currentItem[idField]);
        if (!isExist) {
          cacheExist.set(currentItem[idField], true);
        }

        if (currentItem.parentNode) {
          findParent(currentItem.parentNode);
        }

        if (!isExist) {
          data.push(currentItem);
        }
      };

      me.datax.forEach(item => {
        findParent(item);
      });

      me.datax = data;
    },

    //Lấy dữ liệu của dòng được chọn
    //Created by LTDAT(16.06.2020)
    getRowSelected() {
      const me = this;
      return me.rowSelected;
    },
    //Xử lý sự kiện ấn phím trên row
    //Created by LTDAT(15.06.2020)
    row_keydown(dataRow, e) {
      let me = this;
      let currentIndex = me.rowSelected.index;
      let tr = null;
      e = e || window.event;
      if (e.ctrlKey || e.shiftKey) {
        switch (e.which) {
          case 40: //down
            if (currentIndex < me.datax.length - 1 && !e.altKey) {
              me.shortKeyForQuickSelect = true;
              tr = this.$el.querySelector(`.ms-tbody.data tr[indexRow='${currentIndex}']`);
              currentIndex++;
              me.indexOfRowFocused = currentIndex;
              if (!me.shortkeyQuickSelectedData.includes(me.datax[currentIndex])) {
                me.shortkeyQuickSelectedData.push(me.datax[currentIndex]);
              } else {
                me.shortkeyQuickSelectedData.remove(me.datax[currentIndex]);
              }
              me.rowSelected.data = me.datax[currentIndex];
              me.rowSelected.index = currentIndex;
              me.adjustScroll(currentIndex, tr, false);
              e.preventDefault();
            }
            break;
          case 38: //up
            if (currentIndex > 0 && !e.altKey) {
              me.shortKeyForQuickSelect = true;
              tr = this.$el.querySelector(`.ms-tbody.data tr[indexRow='${currentIndex}']`);
              currentIndex--;
              me.indexOfRowFocused = currentIndex;

              if (!me.shortkeyQuickSelectedData.includes(me.datax[currentIndex])) {
                me.shortkeyQuickSelectedData.push(me.datax[currentIndex]);
              } else {
                me.shortkeyQuickSelectedData.remove(me.datax[currentIndex]);
              }

              me.rowSelected.data = me.datax[currentIndex];
              me.rowSelected.index = currentIndex;
              me.adjustScroll(currentIndex, tr, true);
              e.preventDefault();
            }
            break;
          case 32: //space
            e.target.focus();
            if (me.shortkeyQuickSelectedData && me.shortkeyQuickSelectedData.length > 0) {
              me.shortkeyQuickSelectedData.forEach(item => {
                me.clickMultiple(item);
              });
            } else {
              me.clickMultiple(me.datax[currentIndex]);
            }
            e.preventDefault();
            break;
        }
      } else {
        switch (e.which) {
          case 40: //down
            if (currentIndex < me.datax.length - 1 && !e.altKey) {
              me.shortkeyQuickSelectedData = [];
              tr = this.$el.querySelector(`.ms-tbody.data tr[indexRow='${currentIndex}']`);
              tr.nextElementSibling.querySelector('td').click();
              currentIndex++;
              me.indexOfRowFocused = currentIndex;
              e.target.focus();
              me.rowSelected.data = me.datax[currentIndex];
              me.rowSelected.index = currentIndex;
              me.adjustScroll(currentIndex, tr, true);
              e.preventDefault();
            }
            break;
          case 38: //up
            if (currentIndex > 0 && !e.altKey) {
              me.shortkeyQuickSelectedData = [];
              tr = this.$el.querySelector(`.ms-tbody.data tr[indexRow='${currentIndex}']`);
              tr.previousElementSibling.querySelector('td').click();
              currentIndex--;
              me.indexOfRowFocused = currentIndex;
              e.target.focus();
              me.rowSelected.data = me.datax[currentIndex];
              me.rowSelected.index = currentIndex;
              me.adjustScroll(currentIndex, tr, true);
              e.preventDefault();
            }
            break;
          case 32: //space
            e.target.focus();
            if (me.shortkeyQuickSelectedData && me.shortkeyQuickSelectedData.length > 0) {
              me.shortkeyQuickSelectedData.forEach(item => {
                me.clickMultiple(item);
              });
            } else {
              // Nếu chỉ có 1 dòng, toggle dòng đó
              me.clickMultiple(me.datax[currentIndex]);
            }
            e.preventDefault();
            break;
        }
      }
    },
    quickSelected(e) {
      let me = this;
      let oldIndex = me.oldIndexOfRowFocused || 0;
      if (e.shiftKey) {
        me.shortKeyForQuickSelect = true;
        let trEvent = e.target.closest('.ms-tr');
        let newIndex = trEvent.getAttribute('indexrow');
        let start = oldIndex >= newIndex ? newIndex : oldIndex;
        let end = oldIndex >= newIndex ? oldIndex : newIndex;
        me.shortkeyQuickSelectedData = [];
        for (start; start <= end; start++) {
          me.shortkeyQuickSelectedData.push(me.datax[start]);
        }
      }
      if (e.ctrlKey) {
        me.shortKeyForQuickSelect = true;
        let trEvent = e.target.closest('.ms-tr');
        let newIndex = trEvent.getAttribute('indexrow');
        if (!me.shortkeyQuickSelectedData.includes(me.datax[oldIndex])) {
          me.shortkeyQuickSelectedData.push(me.datax[oldIndex]);
        }
        if (me.shortkeyQuickSelectedData.includes(me.datax[newIndex])) {
          me.shortkeyQuickSelectedData.remove(me.datax[newIndex]);
        } else {
          me.shortkeyQuickSelectedData.push(me.datax[newIndex]);
        }
      }

      document.onkeydown = function () {
        me.$el.classList.add('user-selected-none');
      };
      document.onkeyup = function () {
        me.shortKeyForQuickSelect = false;
        me.$el.classList.remove('user-selected-none');
      };
      document.onmouseup = function () {
        if (!me.shortKeyForQuickSelect) {
          me.shortkeyQuickSelectedData = [];
        }
      };
    },
    destroyQuickSelected(e) {
      let me = this;
      if (me.shortKeyForQuickSelect) {
        me.shortKeyForQuickSelect = false;
      }
    },

    //Sự kiện ấn vào nút chọn tất cả bên trên header
      changeCheckedMultiple(e) {
      const me = this;
      let checked =
        e.target.tagName.toLowerCase() === 'input' ? e.target.checked : false;
       let VModel = [...me.selected];

      // Gán lại VModel để hiện thị ô checkAll
      if (typeof me.assignValueVModelCheckAll == 'function') {
        VModel = me.assignValueVModelCheckAll(checked);
      }

      // xử lý case group row
      if (me.rowSpanGroup && me.groupField && me.groupField.length > 0) {
        if (!checked) {
          VModel = [];
        } else {
          let data = me.datax.filter(
            (i) => !(i.isDisabledCheckBox || i.isDisabledCheckBoxCustom)
          );

          VModel = data;
        }
      } else {
        // Lọc các bản ghi đã có và chưa có trong list selected để update lại
        // không cần gán lại allChecked do có v-model tự binding từ sự kiện click checkbox all
        if (!checked) {
          let data = me.datax.filter(
            (i) => !(i.isDisabledCheckBox || i.isDisabledCheckBoxCustom)
          );
          // NXCHIEN 21.10.2022: trường hợp group thì cần lấy dữ liệu cả cây group
          if (me.groupField && me.groupDataView && me.isShowMultiple) {
            data = _.cloneDeep(me.groupDataView);
          }

          data.forEach((dataRow) => {
            const existed = VModel.find(
              (x) => x[me.idField] === dataRow[me.idField]
            );
            if (existed) {
              VModel.remove(existed);
            }
          });
        } else {
          let data = me.datax.filter(
            (i) => !(i.isDisabledCheckBox || i.isDisabledCheckBoxCustom)
          );
          // NXCHIEN 21.10.2022: trường hợp group thì cần lấy dữ liệu cả cây group
          if (me.groupField && me.groupDataView && me.isShowMultiple) {
            data = _.cloneDeep(me.groupDataView);
          }
          data.forEach((dataRow) => {
            const existed = VModel.find(
              (x) => x[me.idField] === dataRow[me.idField]
            );

            if (!VModel.includes(existed)) {
              VModel.push(dataRow);
            }
          });
        }
      }

      me.$emit('update:selected', VModel);
      me.$emit('onChangeCheckedMultiple', checked);
    },

    changePageSize(value) {
      let me = this;
      //Nếu thực hiện lấy data dưới local thì thức hiện lưu dữ liệu vào internalDataSource và phân trang local
      if (me.queryMode !== 'remote' && me.pagination) {
        this.pageSizeInternal = value;
        // me.$emit('update:pageSize', value);
        me.datax = me.getItemPagination();
      }
      this.$emit('changePageSize', value);
    },
    /**
     * Gán giá trị mặc định cho columnType của cấu hình cột
     * @param {Object} col: cấu hình cột
     * @author NVLAM 18.12.2020
     */
    setDefaultColumnType(col) {
      const me = this,
        FormatType = me.$ms.constant.FormatType;
      switch (col.formatType) {
        case FormatType.List:
          col.columnType = 'ColumnDropdown';
          break;
        case FormatType.Checkbox:
          col.columnType = 'ColumnBoolean';
          break;
        case FormatType.Text:
          if (col.columnType != 'ColumnDropdown') {
            col.columnType = 'ColumnText';
          }
          break;
        case FormatType.Number:
        case FormatType.Quantity:
        case FormatType.QuantityConvert:
        case FormatType.UnitPrice:
        case FormatType.UnitPriceOC:
        case FormatType.AmountOC:
        case FormatType.Amount:
        case FormatType.ExchangeRate:
        case FormatType.Rate:
        case FormatType.Allocation:
          col.columnType = 'ColumnNumber';
          break;
        case FormatType.Date:
          col.columnType = 'ColumnDateTime';
          break;
        case FormatType.DateTime:
        case FormatType.Time:
          col.columnType = 'ColumnDateTime';
          break;
        default:
          col.columnType = 'ColumnText';
          break;
      }
    },
    /**
     * Đọc điều kiện filter header
     */
    getFilterHeader(hasColumn) {
      let me = this,
        result = [],
        enumFilterHeader = me.$ms.constant.FilterOperator,
        columns = me.filterHeader; //me.columnx;
      for (let i = 0; i < columns.length; i++) {
        let column = columns[i];
        if (column.filter && column.filter.value !== column.filterIgnoreValue) {
          let item = null,
            filterValue = column.filter.value,
            operator = column.filter.operator;
          switch (operator) {
            case enumFilterHeader.Between:
              item = [
                {
                  field: column.isExtData
                    ? `JSON_UNQUOTE(JSON_EXTRACT(ExtData, '$.${column.dataField}'))`
                    : column.dataField,
                  operator: enumFilterHeader.GreaterThanEquals,
                  value: column.filter.from,
                },
                {
                  field: column.isExtData
                    ? `JSON_UNQUOTE(JSON_EXTRACT(ExtData, '$.${column.dataField}'))`
                    : column.dataField,
                  operator: me.$ms.constant.FilterHeader.LessThanEquals,
                  value: column.filter.to,
                },
              ];
              break;
            case enumFilterHeader.Null:
            case enumFilterHeader.NotNull:
              item = {
                field: column.isExtData
                  ? `JSON_UNQUOTE(JSON_EXTRACT(ExtData, '$.${column.dataField}'))`
                  : column.dataField,
                operator: operator,
              };

              //với cột text sẽ fix giá trị để lên server sẽ xử lý thêm kiểm tra với giá trị trống ''
              switch (column.columnType) {
                case 'ColumnText':
                case 'ColumnDrilldown':
                  item.value = 'text';
                  break;
              }
              break;
            case enumFilterHeader.Contains:
            case enumFilterHeader.Notcontains:
            case enumFilterHeader.StartsWith:
            case enumFilterHeader.EndsWith:
              if (typeof filterValue !== 'undefined' && filterValue !== null && filterValue !== '') {
                item = {
                  field: column.isExtData
                    ? `JSON_UNQUOTE(JSON_EXTRACT(ExtData, '$.${column.dataField}'))`
                    : column.dataField,
                  operator: operator,
                  value: filterValue,
                };
              }
              break;
            default:
              switch (column.columnType) {
                case 'ColumnDateTime':
                  if (filterValue) {
                    item = {
                      field: column.isExtData
                        ? `JSON_UNQUOTE(JSON_EXTRACT(ExtData, '$.${column.dataField}'))`
                        : column.dataField,
                      value: filterValue, //moment(filterValue).format('YYYY-MM-DD'),
                      operator: operator,
                    };
                  }
                  break;
                default:
                  if (typeof filterValue !== 'undefined' && filterValue !== null && filterValue !== '') {
                    item = {
                      field: column.isExtData
                        ? `JSON_UNQUOTE(JSON_EXTRACT(ExtData, '$.${column.dataField}'))`
                        : column.dataField,
                      value: filterValue,
                      operator: operator,
                    };
                  }
                  break;
              }
              break;
          }

          //xử lý tình huống enum của cột không chứa giá trị thì khỏi filter
          if (item && (column.enumName || column.enum) && !Array.isArray(item)) {
            let enumObj = me.$ms.constant[column.enumName || column.enum],
              hasValue = false;
            for (let i in enumObj) {
              if (enumObj[i] === item.value) {
                hasValue = true;
                break;
              }
            }

            if (!hasValue) {
              item = null;
            }
          }

          //Thêm xử lý lại đoạn Between
          if (item) {
            if (Array.isArray(item)) {
              for (let i = 0; i < item.length; i++) {
                const data = item[i];
                if (hasColumn) {
                  data.column = column;
                }
                if (column.nullToFail) {
                  data.nullToFail = column.nullToFail;
                }

                result.push(data);
              }
            } else {
              if (hasColumn) {
                item.column = column;
              }
              if (column.nullToFail) {
                item.nullToFail = column.nullToFail;
              }

              result.push(item);
            }
          }
        }
      }

      if (me.customFilter) {
        if (Array.isArray(me.customFilter)) {
          result = result.concat(me.customFilter);
        }

        /** Custom lọc grid */
        if (typeof me.customFilter == 'function') {
          result = me.customFilter(result);
        }
      }

      return result;
    },
    /**
     * lưu tất cả các cột đã đánh dấu là đang filter
     */
    columnFilter({col, value}) {
      let me = this;
      let check = me.filterHeader.filter(i => i.dataField == col.dataField);
      if (check.length == 0) {
        me.filterHeader.push(col);
      } else {
        me.filterHeader.forEach(item => {
          if (item.dataField == col.dataField) {
            item = col;
            return;
          }
        });
      }

      //Xóa giá trị filterHeader
      if (!value) {
        this.$nextTick(() => {
          me.filterHeader = me.filterHeader.filter(i => i.dataField !== col.dataField);
        });
      }

      if (me.queryMode !== 'remote') {
        me.localFilter();

        me.setPage(1);
        me.datax = me.getItemPagination();
      } else {
        me.$emit('filter');
      }
    },
    /**
     * Xử lý filter local
     */
    localFilter() {
      const me = this;
      let data = [];

      for (let i = 0; i < me.internalDataSource.length; i++) {
        let record = me.internalDataSource[i];
        let f = true;
        for (let j = 0; j < me.columnx.length; j++) {
          let col = me.columnx[j];
          if (
            col.filter &&
            col.filter.value !== null &&
            typeof col.filter.value !== 'undefined' &&
            col.filter.value !== ''
          ) {
            const fn = this.genFilterFunction([{...col.filter, ...{property: col.dataField}}]);
            let test = fn(record);
            // me.testFilter(
            //   col.filter.operator,
            //   record[col.dataField],
            //   col.filter.value
            // );
            if (!test) {
              f = false;
              break;
            }
          }
        }

        if (!f) {
          continue;
        }
        data.push(record);
      }
      me.localFilterData = data;
      me.totalCount = data.length;
    },
    styleColumn(col, dataRow, styleObject) {
      this.$emit('grid_styleColumn', col, dataRow, styleObject);
    },

    /**
     * hàm conver toán tử thành text: ví dụ "Contants" -> "Chứa"
     */
    buildOperatorText(operator) {
      let operatorText = this.$ms.commonFn.getEnumResource(operator, 'FilterOperator');
      if (operatorText) {
        operatorText = operatorText.toLocaleLowerCase();
      }
      return [' ', operatorText, ' '].join('');
    },
    /**
     * hàm build value filter
     */
    buildValue(item) {
      if (
        item.filter &&
        (item.filter.operator == this.$ms.constant.FilterOperator.Null ||
          item.filter.operator == this.$ms.constant.FilterOperator.NotNull)
      ) {
        return '';
      }
      if (item.filter.value === 0 && item.formatType != this.$ms.constant.FormatType.Enum) {
        return '0';
      }
      let value = this.$ms.format.formatData(item.filter.value, {
        formatType: item.formatType,
        enumName: item.enum,
        dataRow: {},
      });
      return '"' + value + '"';
    },

    /**
     * hàm xóa 1 item filter
     */
    removeFilterItem(item) {
      let me = this;
      this.$nextTick(() => {
        me.filterHeader.remove(item);
      });
      if (
        !item.formatType ||
        item.formatType == me.$ms.constant.FormatType.Text ||
        item.formatType == me.$ms.constant.FormatType.None
      ) {
        item.filter.operator = me.$ms.constant.FilterOperator.Contains;
      } else {
        item.filter.operator = me.$ms.constant.FilterOperator.Equals;
      }
      item.filter.value = null;
      item.hasActionFilter = false;
      let th = me.$refs.th.filter(x => x.col.dataField == item.dataField);
      if (th && th.length > 0) {
        th[0].removeFilter();
      }
    },
    /**
     * hàm bỏ lọc
     */
    clearAllFilter() {
      let me = this;
      //me.filterHeader.removeAll();
      me.filterHeader.forEach(item => {
        if (
          !item.formatType ||
          item.formatType == me.$ms.constant.FormatType.Text ||
          item.formatType == me.$ms.constant.FormatType.None
        ) {
          item.filter.operator = me.$ms.constant.FilterHeader.Contains;
        } else {
          item.filter.operator = me.$ms.constant.FilterHeader.Equals;
        }
        item.filter.value = null;
        item.hasActionFilter = false;
        let th = me.$refs.th.filter(x => x.col.dataField == item.dataField);
        if (th && th.length > 0) {
          th[0].removeFilter();
        }
      });
      me.filterHeader = [];
    },

    /**
     * thêm hàm thiết lập cột ẩn hiện cột trên grid để có thể gọi từ component sử dụng  grid
     * pvduy 02/04/2021
     */
    setColumnVisible(field, visible) {
      const me = this;
      let column = null;
      for (let i = 0; i < me.columns.length; i++) {
        let col = me.columns[i];
        if (col.dataField === field) {
          column = col;
          column.visible = visible;
          break;
        }
      }
      me.initColumns(me.columns);
    },
    //Lấy element scroll
    //Created by LTDAT (16.06.2020)
    getScroller() {
      if (this && this.$el && typeof this.$el.querySelector === 'function') {
        return this.$el.querySelector('.scroller');
      }
      return null;
    },
    /**
     * scroll theo phím up /down
     * @param {Bản ghi đang selected} typeAheadPointer
     * @param {dòng focus trước đó} trOld
     * @param {Scroll Lên hay xuống} scrollUp
     * CreatedBy NNLAM 16.06.2020
     */
    adjustScroll(typeAheadPointer = this.typeAheadPointer, trOld, scrollUp) {
      let scroller = this.getScroller();
      let tr = this.$el.querySelector(`.ms-tbody.data tr[indexRow='${typeAheadPointer}']`);
      let th = this.$el.querySelector('.ms-thead th');
      if (scroller && tr && th) {
        const trRect = tr.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        const headerHeight = th.offsetHeight || 0;

        // Buffer để scroll sớm hơn, đảm bảo dòng luôn hiển thị rõ ràng
        const buffer = 80;

        // Kiểm tra xem dòng có đang hiển thị đầy đủ trong vùng có thể scroll không
        const isRowFullyVisible = trRect.top >= scrollerRect.top + headerHeight && trRect.bottom <= scrollerRect.bottom;

        // Kiểm tra xem dòng có bị che khuất một phần không
        const isRowPartiallyHidden =
          trRect.top < scrollerRect.top + headerHeight || trRect.bottom > scrollerRect.bottom;

        if (!scrollUp) {
          // Scroll xuống: kiểm tra nếu dòng hiện tại gần hoặc nằm dưới vùng hiển thị
          if (trRect.bottom > scrollerRect.bottom - buffer || isRowPartiallyHidden) {
            // Sử dụng scrollIntoView để scroll mượt mà hơn
            tr.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }
        } else {
          // Scroll lên: kiểm tra nếu dòng hiện tại gần hoặc nằm trên vùng hiển thị
          if (trRect.top > scrollerRect.top + headerHeight || isRowPartiallyHidden) {
            // Sử dụng scrollIntoView để scroll mượt mà hơn
            tr.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }
        }
      }
    },

    //   const me = this;
    //   listItems = listItems || [];
    //   //Trong trường hợp không phải grid allow check thì không cần làm gì
    //   if (!me.multiple) {
    //     return;
    //   }

    //   let keyField = null;
    //   if (idField) {
    //     keyField = idField;
    //   } else if (me.idField) {
    //     keyField = me.idField;
    //   }

    //   if (keyField) {
    //     let arrModel = [];

    //     listItems.forEach(id => {
    //       me.datax.forEach(item => {
    //         if (typeof id == "object") {
    //           if (item[keyField] == id[keyField]) {
    //             arrModel.push(item);
    //           }
    //         } else {
    //           if (item[keyField] == id) {
    //             arrModel.push(item);
    //           }
    //         }
    //       });
    //     });

    //     me.$emit("update:selected", arrModel);
    //   }
    // },

    /**
     * Thay đổi lock của column
     */
    columnChangeLock(col, locked) {
      const me = this;
      // let groups = me.columnx.filter(x => x.group === col.caption);
      // if (col.isGroup) {
      //   locked = !col.lock;
      //   col.lock = !col.lock;
      // }
      if (locked) {
        for (let i = 0; i < me.columnx.length; i++) {
          let column = me.columnx[i];
          column['lock'] = true;
          // if (col.isGroup) {
          //   if (groups[groups.length - 1]?.dataField === column.dataField) {
          //     break;
          //   }
          // } else {
          //   if (col === column) {
          //     break;
          //   }
          // }
          if (col === column) {
            break;
          }
        }
      } else {
        for (let i = me.columnx.length - 1; i >= 0; i--) {
          let column = me.columnx[i];
          column['lock'] = false;
          if (col === column) {
            break;
          }
        }
      }

      me.updateStickyOffset();
    },
    /**
     * Lấy style footer
     */
    getFooterStyle(col) {
      let res = {};
      if (typeof col.stickyOffset === 'number') {
        res.left = col.stickyOffset + 'px';
      }

      if (typeof col.width === 'number') {
        res.minWidth = res.width = col.width + 'px';
      }

      return res;
    },
    /**
     * scroll table
     * HTHIEP 28.03.2022: Sửa lỗi không có footer thì bỏ qua
     */
    tableScroll(e) {
      // Kiểm tra nếu có footer thì mới xử lý
      if (!this.footer) {
        return;
      }
      const me = this,
        tableFooter = me.$refs.tableFooter;

      tableFooter.scrollLeft = e.target.scrollLeft;
    },
    /**
     * Lấy dữ liệu data grid
     */
    getData() {
      return this.datax;
    },
    /**
     * Cập nhật lại dòng dữ liệu được focus
     * tbngoc 11.07.2022
     */
    updateSelectedRow(data, key) {
      let me = this;
      let dataGrid = me.getData();
      let newData = dataGrid.find(item => item[key] == data[key]);
      let index = dataGrid.findIndex(item => item[key] == data[key]);
      me.rowSelected.data = newData;
      me.rowSelected.index = index;
    },
    dragChanged(arg) {
      const me = this,
        moved = arg.moved,
        columns = JSON.parse(JSON.stringify(me.columnx));

      let oldIndex = moved.oldIndex,
        newIndex = moved.newIndex;

      if (me.multiple) {
        oldIndex--;
        newIndex--;
      }
      if (me.serial) {
        oldIndex--;
        newIndex--;
      }

      let col = columns[oldIndex];
      columns.remove(col);
      columns.insertAt(newIndex, col);

      // columns.remove(undefined);
      me.initColumns(columns);
      me.updateStickyOffset();
    },

    /**
     * Xử lý sự kiện update:indeterminate từ MsCheckbox
     * @param {Boolean} value - Giá trị indeterminate mới
     */
    updateHeaderIndeterminate(value) {
      this.headerIndeterminate = value;
    },

    /**
     * Cập nhật trạng thái indeterminate cho header checkbox
     */
    updateHeaderIndeterminateState() {
      const me = this;
      if (!me.multiple) return;

      const selectedCount = me.selected ? me.selected.length : 0;
      const availableData = me.datax ? me.datax.filter(item => !item.isDisabledCheckBox) : [];
      const availableCount = availableData.length;

      // Indeterminate khi có một số item được chọn nhưng không phải tất cả
      me.headerIndeterminate = selectedCount > 0 && selectedCount < availableCount;
    },

    /**
     * Xử lý sự kiện khi click vào action của empty state
     */
    handleEmptyStateAction(action) {
      this.$emit('handle-empty-state-action', action);
    },

    /**
     * Xử lý phím tắt ở cấp độ grid để có thể di chuyển focus bằng mũi tên
     * ngay cả khi chưa click vào dòng nào cụ thể
     * @param {Event} event - Sự kiện keydown
     */
    handleGridKeydown(event) {
      // Chỉ xử lý khi nhấn phím mũi tên lên/xuống
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }

      // Nếu đang có dòng được chọn, không cần xử lý gì thêm
      // để tránh duplicate với row_keydown
      if (this.rowSelected && this.rowSelected.data) {
        return;
      }

      // Nếu chưa có dòng nào được chọn, tự động chọn dòng đầu tiên
      if (this.datax && this.datax.length > 0) {
        const firstRow = this.datax[0];
        this.rowSelected = {
          data: firstRow,
          index: 0,
        };

        // Focus vào dòng đầu tiên
        this.$nextTick(() => {
          const firstTr = this.$el.querySelector('.ms-tbody.data tr');
          if (firstTr) {
            firstTr.focus();
          }
        });
      }

      // Ngăn chặn event bubbling để tránh duplicate
      event.stopPropagation();
      event.preventDefault();
    },

    /**
     * Đảm bảo grid luôn có thể nhận focus và sử dụng phím tắt
     * Gọi method này khi cần force focus vào grid
     */
    ensureGridFocus() {
      if (this.isAutoFocusFirstRow && this.datax && this.datax.length > 0) {
        this.$nextTick(() => {
          // Kiểm tra xem grid có đang focus không
          if (!this.$el.contains(document.activeElement)) {
            // Nếu không có dòng nào được focus, focus vào dòng đầu tiên
            if (!this.rowSelected || !this.rowSelected.data) {
              this.autoSelectedFirstRow();
            } else {
              // Nếu có dòng được chọn, focus vào dòng đó
              const tr = this.$el.querySelector(`.ms-tbody.data tr[indexRow='${this.rowSelected.index}']`);
              if (tr) {
                tr.focus();
              } else {
                // Nếu không tìm thấy dòng được chọn, focus vào dòng đầu tiên
                this.autoSelectedFirstRow();
              }
            }
          }
        });
      }
    },

    /**
     * Xử lý khi window được focus lại (sau khi đóng form)
     * Đảm bảo grid luôn có thể nhận focus và sử dụng phím tắt
     */
    handleWindowFocus() {
      // Chỉ xử lý khi grid đang hiển thị và có dữ liệu
      if (this.isAutoFocusFirstRow && this.datax && this.datax.length > 0) {
        // Đợi một chút để đảm bảo DOM đã được cập nhật
        setTimeout(() => {
          this.ensureGridFocus();
        }, 100);
      }
    },

    /**
     * Force focus vào grid để đảm bảo phím tắt luôn hoạt động
     * Gọi method này khi cần đảm bảo grid có thể nhận focus
     */
    forceGridFocus() {
      if (this.isAutoFocusFirstRow && this.datax && this.datax.length > 0) {
        // Đảm bảo grid container có thể nhận focus
        this.$el.focus();

        // Sau đó focus vào dòng phù hợp
        this.$nextTick(() => {
          this.ensureGridFocus();
        });
      }
    },

    /**
     * Đồng bộ selected state cho toàn bộ row span group
     * Khi một row được select, tất cả rows trong cùng group cũng được apply class selected
     */
    syncRowSpanGroupSelectedState() {
      const me = this;

      // Chỉ xử lý khi ở row span group mode
      if (!me.rowSpanGroup || !me.groupField || me.groupField.length === 0) {
        return;
      }

      // Xóa tất cả class row-selected trước
      const allRows = me.$el.querySelectorAll('.ms-tbody.data tr.ms-tr');
      allRows.forEach((row) => {
        row.classList.remove('row-selected');
      });

      // Nếu có row được selected, apply class cho tất cả rows trong group
      if (me.rowSelected && me.rowSelected.data && me.groupField[0]) {
        const groupId = me.rowSelected.data[me.groupField[0]] + "=+*" + me.rowSelected.data[me.groupField[1]]
        if (groupId) {
          const groupRows = me.$el.querySelectorAll(
            `.ms-tbody.data tr[data-rowspan-group="${groupId}"]`
          );
          groupRows.forEach((row) => {
            row.classList.add('row-selected');
          });
        }
      }
    },
  },
  beforeUnmount() {
    const me = this;
    document.removeEventListener('click', me.clickOutGrid);
    window.removeEventListener('resize', me.updateSize);
    window.removeEventListener('focus', me.handleWindowFocus);
  },
};
</script>
<style lang="scss">
@import '@/assets/scss/components/MsGridViewer.scss';

.grid-scroll {
  .color-green {
    color: $primary !important;
    font-weight: 600;
  }
}

.table-footer {
  &.visibility {
    visibility: hidden;
    height: 0;
  }
  .footer-total-count {
    font-weight: 400;
    white-space: nowrap;
  }
  .footer-total-summary {
  }
}

// Đảm bảo grid có thể nhận focus
.ms-grid-viewer {
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }
}
</style>
